import { useQueryClient } from "@tanstack/react-query";
import type { ReferenceReport } from "~/api/model";
import type { ReferenceReportUploadStartRequestContentType } from "~/api/model/referenceReportUploadStartRequestContentType";
import {
  getListDemoCasesQueryKey,
  useCompleteDemoCaseReferenceReportUpload,
  useGetDemoCaseReferenceReportPartUrl,
  useStartDemoCaseReferenceReportUpload,
} from "~/api/organizations/organizations";
import { useMultipartFileUpload } from "~/features/settings/hooks/useMultipartFileUpload";
import {
  REFERENCE_REPORT_UPLOAD_TYPES,
  resolveUploadContentType,
} from "~/utils/uploadContentType";

export const getDemoCaseReferenceReportContentType = (
  contentType: string,
  filename: string
): ReferenceReportUploadStartRequestContentType => {
  return resolveUploadContentType(
    contentType,
    filename,
    REFERENCE_REPORT_UPLOAD_TYPES,
    `Unsupported reference report file type: ${contentType}`
  );
};

export const useDemoCaseReferenceReportUpload = (
  organizationId: string,
  demoCaseId: string
) => {
  const { mutateAsync: startUpload } = useStartDemoCaseReferenceReportUpload();
  const { mutateAsync: getPartUrl } = useGetDemoCaseReferenceReportPartUrl();
  const { mutateAsync: completeUpload } =
    useCompleteDemoCaseReferenceReportUpload();
  const queryClient = useQueryClient();

  return useMultipartFileUpload<ReferenceReport>({
    startUpload: async (uploadId, file) =>
      startUpload({
        organizationId,
        demoCaseId,
        uploadId,
        data: {
          contentType: getDemoCaseReferenceReportContentType(
            file.type,
            file.name
          ),
          filename: file.name,
        },
      }),
    getPartUrl: async (uploadId, key, s3UploadId, partNumber) =>
      getPartUrl({
        organizationId,
        demoCaseId,
        uploadId,
        data: { key, uploadId: s3UploadId, partNumber },
      }),
    completeUpload: async (
      uploadId,
      key,
      s3UploadId,
      filename,
      contentType,
      sizeBytes,
      parts
    ) => {
      const report = await completeUpload({
        organizationId,
        demoCaseId,
        uploadId,
        data: {
          key,
          uploadId: s3UploadId,
          filename,
          contentType: getDemoCaseReferenceReportContentType(
            contentType,
            filename
          ),
          sizeBytes,
          parts,
        },
      });
      return report;
    },
    onUploadComplete: async () => {
      await queryClient.invalidateQueries({
        queryKey: getListDemoCasesQueryKey(organizationId),
      });
    },
  });
};
